// CatalogModal — create / rename a product catalog.
//
// PUT /v1/catalogs/:id is a FULL-ROW replacement and 400s if description,
// sort_order or active are omitted, rather than guessing and silently blanking
// them. This form always holds all four fields, so it always sends a complete
// body — which is the reason it is a form rather than an inline rename box.
import { useState, type FormEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { Modal } from "@/components/UI";
import { apiErrorMessage } from "@/utils/apiError";
import { useUI } from "@/store/ui";
import {
  createCatalog,
  updateCatalog,
  type Catalog,
  type CatalogWritePayload,
} from "@/api/catalogs";

export function CatalogModal({
  companyId,
  catalog,
  open,
  onClose,
}: {
  companyId: string;
  catalog: Catalog | null; // null = create
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const addToast = useUI((s) => s.addToast);
  const [name, setName] = useState(catalog?.name ?? "");
  const [description, setDescription] = useState(catalog?.description ?? "");
  const [sortOrder, setSortOrder] = useState(String(catalog?.sort_order ?? 0));
  const [active, setActive] = useState(catalog?.active ?? true);
  const [error, setError] = useState<string | null>(null);

  const seedKey = catalog?.id ?? "new";
  const [lastSeed, setLastSeed] = useState(seedKey);
  if (open && lastSeed !== seedKey) {
    setName(catalog?.name ?? "");
    setDescription(catalog?.description ?? "");
    setSortOrder(String(catalog?.sort_order ?? 0));
    setActive(catalog?.active ?? true);
    setError(null);
    setLastSeed(seedKey);
  }

  const mutation = useMutation({
    mutationFn: (payload: CatalogWritePayload) =>
      catalog ? updateCatalog(catalog.id, payload) : createCatalog(companyId, payload),
    onSuccess: async (saved) => {
      addToast({
        title: catalog ? "Catalog saved" : "Catalog created",
        body: saved?.name,
      });
      await qc.invalidateQueries({ queryKey: ["catalogs", companyId] });
      onClose();
    },
    onError: (err) =>
      setError(apiErrorMessage(err, "Could not save the catalog. Try again.")),
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (name.trim() === "") {
      setError("Name can't be empty.");
      return;
    }
    const order = parseInt(sortOrder, 10);
    mutation.mutate({
      name: name.trim(),
      description,
      sort_order: Number.isNaN(order) ? 0 : order,
      active,
    });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      busy={mutation.isPending}
      title={catalog ? "Edit catalog" : "New catalog"}
      foot={
        <>
          <button
            className="btn ghost"
            type="button"
            onClick={onClose}
            disabled={mutation.isPending}
          >
            Cancel
          </button>
          <div className="flex-1" />
          <button
            className="btn accent"
            type="submit"
            form="catalog-form"
            disabled={mutation.isPending}
          >
            {mutation.isPending
              ? "Saving…"
              : catalog
                ? "Save changes"
                : "Create catalog"}
          </button>
        </>
      }
    >
      <form id="catalog-form" onSubmit={handleSubmit} className="stack-16">
        <div className="field">
          <label className="label" htmlFor="c-name">
            Name
          </label>
          <input
            id="c-name"
            className="input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Solar EPC 2026"
            maxLength={200}
            autoFocus
          />
          <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
            Unique within this merchant, compared case-insensitively. Other
            merchants can reuse the same name.
          </div>
        </div>
        <div className="field">
          <label className="label" htmlFor="c-desc">
            Description
          </label>
          <textarea
            id="c-desc"
            className="input"
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        <div className="field" style={{ width: 140 }}>
          <label className="label" htmlFor="c-sort">
            Sort order
          </label>
          <input
            id="c-sort"
            className="input"
            type="number"
            step={1}
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
          />
          <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
            Lower first; ties break by name.
          </div>
        </div>
        <label
          className="row gap-8"
          htmlFor="c-active"
          style={{ cursor: "pointer", alignItems: "center" }}
        >
          <input
            id="c-active"
            type="checkbox"
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
          />
          <span style={{ fontSize: 13 }}>
            {active
              ? "Active"
              : "Inactive — hidden from pickers. Its products stay usable."}
          </span>
        </label>

        {error && (
          <div style={{ color: "var(--neg)", fontSize: 12 }}>{error}</div>
        )}
      </form>
    </Modal>
  );
}
